import React, { useCallback, useRef, useState } from 'react';
import { Grid, Typography, CircularProgress } from '@material-ui/core/';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ButtonMUI from '@material-ui/core/Button';
import {
  Edit,
  Filter,
  FormTab,
  TextInput,
  TabbedForm,
  TopToolbar,
  List,
  Button as ButtonReactAdmin,
  useGetList,
  useEditController,
  useNotify,
  useRefresh
} from 'react-admin';
import { FormSpy, useFormState } from 'react-final-form';
import omit from 'lodash/omit';
import get from 'lodash/get';
import { saveAs } from 'file-saver';

import EditToolbar from '../../core/components/edit/EditToolbar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import Empty from '../../core/components/list/Empty';
import { ListPagination } from '../../core/components/list/ListPagination';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import WrapperField from '../../core/components/WrapperField';
import {
  getInitials,
  predictiveSearchFormatter,
  predictiveSearchParser,
  removePathEnd
} from '../../core/utils';
import FundInvestorStepper from '../fundinvestors/FundInvestorStepper';
import FundCreateDialog from './FundCreateDialog';
import WireInstructionCreateUpdateDialog from './wireinstructions/WireInstructionCreateUpdateDialog';
import FundInvestorDatagrid from './FundInvestorDatagrid';
import InvestorContactsDatagrid from './InvestorContactsDatagrid';
import InvestorDetails from './InvestorDetails';
import WireInstructionsDatagrid from './wireinstructions/WireInstructionsDatagrid';
import CopyContactStepper from './CopyContactStepper';
import MatchingFields from './MatchingFields';
import InvestorListBulkActions from './InvestorListBulkActions';
import FundInvestorApi from '../../core/apis/FundInvestorApi';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import ViewTab from '../../core/components/forked/layout/ViewTab';
import InvestorFundRelationshipDialog from '../investors/InvestorFundRelationshipDialog';
import { ExcelIcon } from '../../core/themes/icons/ExcelIcon';
import InvestorApi from '../../core/apis/InvestorApi';
import UdfTab from '../../core/components/tab/UdfTab';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import WireInstructionsImportFromExcel from './wireinstructions/WireInstructionsImportFromExcel';
import PrimaryContactsDialog from './primarycontacts/PrimaryContactsDialog';
import ForceListRender from '../../core/components/forked/list/ForceListRender';
import TagFilter from '../../core/components/inputs/TagFilter';
import { isEmpty } from 'lodash';
import InvestorTagsAssociationsApi from '../../core/apis/InvestorTagsAssociationsApi';

const RELATED_FUNDS_TAB_PATH = '1';
const RELATED_CONTACTS_TAB_PATH = '2';
const RELATED_WIRE_INSTRUCTIONS = '4';
const INVESTOR_UDF_TAB_PATH = '5';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  },
  simpleForm: {
    '& > div': {
      '& > .ra-input': {
        height: 'auto !important',
        '& > div': {
          width: '100%'
        }
      }
    }
  },
  progress: {
    position: 'absolute',
    top: '25%',
    left: '55%'
  }
});

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '8px'
  },
  ButtonMUISpan: {
    paddingLeft: '4px'
  },
  circularProgress: {
    paddingRight: '3px'
  },
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  }
});

const ActionsStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  }
};

const useActionsStyles = makeStyles(ActionsStyles);

const YN_FREEZEINVESTORS = 'YN_freezeInvestors';

const FundFilters = ({
  fundInvTagsList,
  fundInvSelectedTags,
  setFundInvSelectedTags,
  filterValues,
  ...props
}) => {
  return (
    <Filter variant="standard" {...props}>
      <TextInput
        source="fundName"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        label="Search by Fund Name"
      />
      <TextInput
        source="fundId"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        label="Search by Fund ID"
        options={{ autoComplete: 'off' }}
      />
      {fundInvTagsList.length > 0 && (
        <TagFilter
          alwaysOn
          source="fundInvestorTagId"
          tagsList={[]}
          selectedTags={[]}
          fundInvTagsList={fundInvTagsList}
          fundInvSelectedTags={fundInvSelectedTags}
          onChange={() => null}
          displayTagFilter={false}
          onChangeFundInv={e => setFundInvSelectedTags(e)}
          displayFundInvTagFilter={true}
          isListFilter={true}
          selectLabel="Fund-Investor Tags"
          topLabel="Fund-Investor Tags"
          filterValues={filterValues}
        />
      )}
    </Filter>
  );
};

const ContactFilters = props => (
  <Filter variant="standard" {...props}>
    <TextInput
      source="lastName"
      alwaysOn
      parse={predictiveSearchParser}
      format={predictiveSearchFormatter}
      label="Search by Last Name"
      options={{ autoComplete: 'off' }}
    />
  </Filter>
);

const FundInvestorActions = withStyles(ActionsStyles)(
  ({
    classes,
    hasExportInvestorFundsPermission,
    hasCreateFundPermission,
    hasWriteFundInvestorPermission,
    investorId,
    filterValues,
    ...props
  }) => {
    const [submitting, setSubmitting] = useState(false);
    const classesExportButton = useStyles();

    const handleClickDownloadRelatedFundsAsExcel = async (
      investorId,
      filters
    ) => {
      setSubmitting(true);
      FundInvestorApi.getRelatedFundsAsExcel(investorId, filters).then(resp => {
        const fileName = 'Investor Related Funds.xlsx';
        saveAs(resp, fileName);
        setSubmitting(false);
      });
    };

    return (
      <TopToolbar>
        {hasExportInvestorFundsPermission && (
          <div>
            <ButtonMUI
              aria-controls="simple-menu"
              aria-haspopup="true"
              disabled={submitting}
              onClick={() =>
                handleClickDownloadRelatedFundsAsExcel(investorId, filterValues)
              }
            >
              <div className={classesExportButton.WrapperClass}>
                {submitting && (
                  <span className={classesExportButton.circularProgress}>
                    <CircularProgress size={'15px'} />
                  </span>
                )}
                <ExcelIconImage />
                <span className={classesExportButton.ButtonMUISpan}>
                  Export Funds to Excel
                </span>
              </div>
            </ButtonMUI>
          </div>
        )}
        {hasCreateFundPermission && hasWriteFundInvestorPermission && (
          <ButtonReactAdmin
            color="primary"
            onClick={props.handleFundCreateButtonClick}
            label="Add New Fund"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
        {hasWriteFundInvestorPermission && (
          <ButtonReactAdmin
            color="primary"
            onClick={props.handleFundInvestorCreateButtonClick}
            label="Add Existing Fund"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
      </TopToolbar>
    );
  }
);

const CreateWireInstructionButton = ({
  handleWireInstructionCreateButtonClick,
  hasEditPermission,
  hasCreateWireInstructionPermission
}) => {
  const classes = useActionsStyles();
  const formState = useFormState();
  if (hasEditPermission && hasCreateWireInstructionPermission) {
    const isOrphan = get(formState, 'values.isOrphan', true);
    return (
      <Tooltip
        placement="top"
        disableHoverListener={!isOrphan}
        disableFocusListener
        disableTouchListener
        title="In order to add Wire Instructions, please assign at least one fund to the investor"
      >
        <div>
          <ButtonReactAdmin
            color="primary"
            onClick={handleWireInstructionCreateButtonClick}
            label="Add New Wire Instruction"
            className={classes.addButton}
            disableFocusRipple
            disabled={isOrphan}
          />
        </div>
      </Tooltip>
    );
  }

  return null;
};

const WireInstructionsActions = withStyles(ActionsStyles)(
  ({
    entityId,
    handleWireInstructionCreateButtonClick,
    hasEditPermission,
    hasCreateWireInstructionPermission,
    hasExportWireInstructionExcelPermission,
    dataAvailable
  }) => (
    <TopToolbar>
      <WireInstructionsImportFromExcel
        hasImportWireInstructionExcelPermission={false}
        hasExportWireInstructionExcelPermission={
          hasExportWireInstructionExcelPermission
        }
        entityId={entityId}
        dataAvailable={dataAvailable}
      />
      <CreateWireInstructionButton
        handleWireInstructionCreateButtonClick={
          handleWireInstructionCreateButtonClick
        }
        hasEditPermission={hasEditPermission}
        hasCreateWireInstructionPermission={hasCreateWireInstructionPermission}
      />
    </TopToolbar>
  )
);

const ExcelIconImage = withStyles(styles)(({ classes, icon }) => {
  return <ExcelIcon className={classes.excelIcon}>{icon}</ExcelIcon>;
});

const InvestorContactsActions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  hasExportInvestorContactsPermission,
  showCopyContactsStepper,
  showFilter,
  showPrimaryContacts,
  investorId,
  hasEditPermission,
  hasWriteAuthorizationPermission,
  userProfile = {},
  ids = [],
  ...rest
}) => {
  const [submitting, setSubmitting] = useState(false);
  const classes = useStyles();
  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleClickDownloadAsExcel = async (investorId, filters) => {
    setSubmitting(true);
    InvestorApi.getRelatedContactsAsExcel(investorId, filters).then(resp => {
      const fileName = 'Investor Related Contacts.xlsx';
      saveAs(resp, fileName);
      setSubmitting(false);
    });
  };
  return (
    <TopToolbar>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
      {ids.length > 0 && hasExportInvestorContactsPermission && (
        <div>
          <ButtonMUI
            aria-controls="simple-menu"
            aria-haspopup="true"
            disabled={submitting}
            onClick={() => handleClickDownloadAsExcel(investorId, filterValues)}
          >
            <div className={classes.WrapperClass}>
              {submitting && (
                <span className={classes.circularProgress}>
                  <CircularProgress size={'15px'} />
                </span>
              )}
              <ExcelIconImage />
              <span className={classes.ButtonMUISpan}>
                Export Contacts to Excel
              </span>
            </div>
          </ButtonMUI>
        </div>
      )}
      {ids.length > 0 &&
        hasEditPermission &&
        userProfile.userType !== 'INVESTOR_ADMIN' && (
          <ButtonReactAdmin
            color="primary"
            onClick={showPrimaryContacts}
            label="Manage Primary Contacts"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
      {ids.length > 0 &&
        hasEditPermission &&
        hasWriteAuthorizationPermission &&
        userProfile.userType !== 'INVESTOR_ADMIN' && (
          <ButtonReactAdmin
            color="primary"
            onClick={showCopyContactsStepper}
            label="Copy Contacts"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
    </TopToolbar>
  );
};

const TabSectionLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

const InvestorTitle = ({ record }) => {
  return (
    <FormTitleBar
      recordLabel={'Investor ID: ' + record.id}
      name={record.name}
      status={record.status}
      statusLabel={'Status:'}
      showAvatar={true}
      avatarContent={getInitials(record.name)}
    />
  );
};

const RelatedFundsWrapper = props => {
  const [openDialog, setOpenDialog] = useState(false);
  const [fundInvestor, setFundInvestor] = useState({});
  const [freezeEnabled, setFreezeEnabled] = useState(false);
  const notifyServerError = useNotifyServerError();

  const [fundInvSelectedTags, setFundInvSelectedTags] = useState([]);
  const { data, loading } = useGetList('fundinvestortags', null, null);
  const fundInvTagsList = Object.values(data);

  const handleDialogOpen = async selectedId => {
    const ids = selectedId.split(':');
    const shouldReturnTags = !isEmpty(data);
    try {
      const fetchedFundInvestor = await FundInvestorApi.get(
        ids[0],
        ids[1],
        shouldReturnTags
      );
      const freezeEnabledConfigInfo = await TenantConfigurationApi.get(
        YN_FREEZEINVESTORS
      ); //.then(info => {
      const freezeValue =
        freezeEnabledConfigInfo &&
        freezeEnabledConfigInfo.pageData[0] &&
        freezeEnabledConfigInfo.pageData[0].value === 'Y';
      setFundInvestor(fetchedFundInvestor);
      setFreezeEnabled(freezeValue);
      setOpenDialog(true);
    } catch (error) {
      notifyServerError(error);
    }
  };
  const { handleReload, classes } = props;

  const handleClose = () => {
    setOpenDialog(false);
    handleReload();
  };

  if (loading) {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <ForceListRender
        style={{ margin: '0px', width: '100%' }}
        filters={
          <FundFilters
            fundInvTagsList={fundInvTagsList}
            fundInvSelectedTags={fundInvSelectedTags}
            setFundInvSelectedTags={setFundInvSelectedTags}
          />
        }
        pagination={<ListPagination />}
        filter={{
          investorId: props.investorId,
          returnFundInvestorTags: fundInvTagsList.length > 0
        }}
        sort={{ field: 'fundName', order: 'ASC' }}
        basePath="/fundinvestors"
        resource="fundinvestors"
        forceShowList={!!props.showFundInvestorCreateDialog} // React-Admin 3.17+ ListView renders an empty page if total fetched = 0, we need to let it know that we have things to render
        title=" " // this is necessary because framework was adding defaultTitle to form header
        actions={
          <FundInvestorActions
            investorId={props.investorId}
            handleFundCreateButtonClick={props.handleFundCreateButtonClick}
            handleFundInvestorCreateButtonClick={
              props.handleFundInvestorCreateButtonClick
            }
            hasExportInvestorFundsPermission={
              props.hasExportInvestorFundsPermission
            }
            hasCreateFundPermission={props.hasCreateFundPermission}
            hasWriteFundInvestorPermission={
              props.hasWriteFundInvestorPermission
            }
          />
        }
        bulkActionButtons={
          (props.hasDeleteFundInvestorPermission ||
            props.hasReadFundInvestorPermission) && (
            <InvestorListBulkActions
              hasReadFundInvestorPermission={
                props.hasReadFundInvestorPermission
              }
              hasDeleteFundInvestorPermission={
                props.hasDeleteFundInvestorPermission
              }
              handleRelationshipOpen={handleDialogOpen}
              total={props.total}
            />
          )
        }
        empty={
          <Empty
            emptyText="There are no related Funds to be displayed"
            buttons={[
              {
                label: 'Add New Fund',
                onClick: props.handleFundCreateButtonClick,
                hide: !(
                  props.hasCreateFundPermission &&
                  props.hasWriteFundInvestorPermission
                )
              },
              {
                label: 'Add Existing Fund',
                onClick: props.handleFundInvestorCreateButtonClick,
                hide: !props.hasWriteFundInvestorPermission
              }
            ]}
          />
        }
      >
        <WrapperField>
          {openDialog ? (
            <InvestorFundRelationshipDialog
              openDialog={openDialog}
              fundInvestor={fundInvestor}
              handleClose={handleClose}
              hasWriteFundInvestorPermission={
                props.hasWriteFundInvestorPermission
              }
              hasEditFundInvestorTagPermission={
                props.hasEditFundInvestorTagPermission
              }
              freezeEnabled={freezeEnabled}
            />
          ) : (
            <></>
          )}
          <FundInvestorDatagrid
            handleInvestorFundEditButtonClick={
              props.handleInvestorFundEditButtonClick
            }
            permissions={props.permissions}
            fundInvTagsList={fundInvTagsList}
          />
          <FundInvestorStepper
            showDialog={props.showFundInvestorCreateDialog}
            handleDialogToggle={props.handleDialogToggle}
            investorId={props.investorId}
            {...props}
          />
        </WrapperField>
      </ForceListRender>
    </>
  );
};

const firstNameSort = { field: 'firstName', order: 'ASC' };

const InvestorEdit = props => {
  const {
    classes,
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    onFormInit,
    ...rest
  } = props;
  const [showFundCreateDialog, setShowFundCreateDialog] = useState(false);
  const [
    showWireInstructionCreateDialog,
    setShowWireInstructionCreateDialog
  ] = useState(false);
  const [
    showWireInstructionUpdateDialog,
    setShowWireInstructionUpdateDialog
  ] = useState(false);
  const [
    showFundInvestorCreateDialog,
    setShowFundInvestorCreateDialog
  ] = useState(false);
  const [showCopyContactsStepper, setShowCopyContactsStepper] = useState(false);
  const [showPrimaryContacts, setShowPrimaryContacts] = useState(false);
  const [wireInstructionRecord, setWireInstructionRecord] = useState({});
  const [wireInstructionPresent, setWireInstructionPresent] = useState(false);
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const [reload, setReload] = useState(false);
  const [hasErrorOnSubmit, setHasErrorOnSubmit] = useState(false);
  const hasModificationSubmittedRef = useRef(false);

  const { record } = useEditController(props);
  const notifyServerError = useNotifyServerError();
  const notify = useNotify();
  const refresh = useRefresh();

  const investorId = rest.id;
  const hideToolbar =
    !props.match.isExact &&
    (rest.location.pathname.slice(-1) === RELATED_FUNDS_TAB_PATH || // Hide save/cancel toolbar in case of related funds/contacts tab
      rest.location.pathname.slice(-1) === RELATED_CONTACTS_TAB_PATH ||
      rest.location.pathname.slice(-1) === RELATED_WIRE_INSTRUCTIONS ||
      rest.location.pathname.slice(-1) === INVESTOR_UDF_TAB_PATH);
  const handleShowCopyContactsStepper = () =>
    setShowCopyContactsStepper(prev => !prev);
  const handleShowPrimaryContacts = () => setShowPrimaryContacts(prev => !prev);
  const handleFundCreateDialogToggle = () =>
    setShowFundCreateDialog(prev => !prev);
  const handleWireInstructionCreateDialogToggle = () =>
    setShowWireInstructionCreateDialog(prev => !prev);
  const handleWireInstructionUpdateDialogToggle = record => {
    setShowWireInstructionUpdateDialog(prev => !prev);
    setWireInstructionRecord(record);
  };
  const handleWireInstructionPresent = present =>
    setWireInstructionPresent(present);
  const handleReload = () => setReload(prev => !prev);
  const handleFundInvestorCreateDialogToggle = () =>
    setShowFundInvestorCreateDialog(prev => !prev);
  const handleFundInvestorCreateButtonClick = () =>
    setShowFundInvestorCreateDialog(prev => !prev);

  // Unblock navigation before deleting
  const onBeforeDelete = () => setWhenBlockNavigation(false);

  const hasCreateFundPermission = permissions.includes('AUTH_CREATE_FUND');
  const hasReadFundInvestorPermission = permissions.includes(
    'AUTH_READ_FUND_INVESTOR'
  );
  const hasWriteFundInvestorPermission = permissions.includes(
    'AUTH_WRITE_FUND_INVESTOR'
  );
  const hasDeleteFundInvestorPermission = permissions.includes(
    'AUTH_DELETE_FUND_INVESTOR'
  );
  const hasExportInvestorContactsPermission = permissions.includes(
    'AUTH_EXPORT_INVESTOR_CONTACTS'
  );
  const hasReadWireInstructionsPermission = permissions.includes(
    'AUTH_READ_WIRE_INSTRUCTION'
  );
  const hasCreateWireInstructionPermission = permissions.includes(
    'AUTH_CREATE_WIRE_INSTRUCTION'
  );
  const hasExportInvestorFundsPermission = permissions.includes(
    'AUTH_EXPORT_INVESTOR_FUNDS'
  );
  const hasDeleteWireInstructionPermission = permissions.includes(
    'AUTH_DELETE_WIRE_INSTRUCTION'
  );
  const hasExportWireInstructionExcelPermission = permissions.includes(
    'AUTH_EXPORT_WIRE_INSTRUCTION_EXCEL'
  );
  const hasWriteAuthorizationPermission = permissions.includes(
    'AUTH_WRITE_USER_AUTHORIZATION'
  );
  const hasReadUserPermission = permissions.includes('AUTH_READ_USER_NA');
  const hasEditInvestorTagPermission = permissions.includes(
    'AUTH_EDIT_INVESTOR_TAG'
  );
  const hasEditFundInvestorTagPermission = permissions.includes(
    'AUTH_EDIT_FUND_INVESTOR_TAG'
  );

  const onSubmitEnd = useCallback(() => {
    if (!hasErrorOnSubmit && hasModificationSubmittedRef) {
      notify('ra.notification.custom_updated_message');
      hasModificationSubmittedRef.current = false;
    }
    refresh();
  }, [hasErrorOnSubmit, notify, refresh]);

  const handleSubmit = useCallback(
    async values => {
      const recordStringify = JSON.stringify(omit(record, 'investorTags'));
      const valuesStringify = JSON.stringify(omit(values, 'investorTags'));
      const tagAssociationsToAdd = Object.keys(values.investorTags)
        .filter(tagId => !Object.keys(record.investorTags).includes(tagId))
        .map(investorTagId => ({ investorId, investorTagId }));

      let tagAssociationsToRemove = [];
      Object.entries(values.investorTags).forEach(([investorTagId, name]) => {
        if (name === null) {
          tagAssociationsToRemove.push({
            investorId,
            investorTagId
          });
        }
      });

      setWhenBlockNavigation(false);

      try {
        if (recordStringify !== valuesStringify) {
          hasModificationSubmittedRef.current = true;
          await InvestorApi.updateInvestor(
            record.id,
            omit(values, 'investorTags')
          );
        }
        if (tagAssociationsToAdd.length > 0) {
          hasModificationSubmittedRef.current = true;
          await InvestorTagsAssociationsApi.create(tagAssociationsToAdd);
        }
        if (tagAssociationsToRemove.length > 0) {
          hasModificationSubmittedRef.current = true;
          await InvestorTagsAssociationsApi.delete(tagAssociationsToRemove);
        }
      } catch (err) {
        setHasErrorOnSubmit(true);
        notifyServerError(err, undefined);
      } finally {
        onSubmitEnd();
      }
    },
    [investorId, notifyServerError, onSubmitEnd, record]
  );

  return (
    <React.Fragment key={reload}>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>

      <Edit
        {...omit(rest, ['userProfile'])}
        undoable={false}
        title={<InvestorTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <TabbedForm
          save={handleSubmit}
          toolbar={
            hideToolbar ? (
              <></>
            ) : (
              <EditToolbar
                {...rest}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={onBeforeDelete}
              />
            )
          }
          variant="standard"
          margin="normal"
        >
          <FormTab label="Investor Details">
            <InvestorDetails
              hasEditInvestorTagPermission={hasEditInvestorTagPermission}
            />
            <FormSpy
              subscription={{ dirty: true }}
              onChange={props => {
                setWhenBlockNavigation(props.dirty);
              }}
            />
          </FormTab>
          <ViewTab label="Related Funds">
            <TabSectionLabel label="Related Funds" />
            <RelatedFundsWrapper
              classes={classes}
              reference="fundinvestors"
              filter={{ investorId }}
              handleFundCreateButtonClick={handleFundCreateDialogToggle}
              handleFundInvestorCreateButtonClick={
                handleFundInvestorCreateButtonClick
              }
              hasCreateFundPermission={hasCreateFundPermission}
              hasReadFundInvestorPermission={hasReadFundInvestorPermission}
              hasWriteFundInvestorPermission={hasWriteFundInvestorPermission}
              hasExportInvestorFundsPermission={
                hasExportInvestorFundsPermission
              }
              hasDeleteFundInvestorPermission={hasDeleteFundInvestorPermission}
              hasDeletePermission={hasDeletePermission}
              hasEditPermission={hasEditPermission}
              hasEditFundInvestorTagPermission={
                hasEditFundInvestorTagPermission
              }
              investorId={investorId}
              handleDialogToggle={handleFundInvestorCreateDialogToggle}
              permissions={permissions}
              showFundInvestorCreateDialog={showFundInvestorCreateDialog}
              handleReload={handleReload}
              {...rest}
            />
            <FundCreateDialog
              title=" "
              showDialog={showFundCreateDialog}
              handleDialogToggle={handleFundCreateDialogToggle}
              {...rest}
            />
          </ViewTab>
          {(userProfile.userType !== 'INVESTOR_ADMIN' ||
            (userProfile.userType === 'INVESTOR_ADMIN' &&
              hasReadUserPermission)) && (
            <ViewTab label="Related Contacts">
              <TabSectionLabel label="Related Contacts" />
              <List
                style={{ margin: '0px', width: '100%' }}
                filters={<ContactFilters />}
                pagination={<ListPagination />}
                filter={{ investorId }}
                basePath="/investorscontacts"
                sort={firstNameSort}
                resource="investorcontacts"
                title=" " // this is necessary because framework was adding defaultTitle to form header
                actions={
                  <InvestorContactsActions
                    hasExportInvestorContactsPermission={
                      hasExportInvestorContactsPermission
                    }
                    investorId={investorId}
                    showCopyContactsStepper={handleShowCopyContactsStepper}
                    showPrimaryContacts={handleShowPrimaryContacts}
                    hasEditPermission={hasEditPermission}
                    hasWriteAuthorizationPermission={
                      hasWriteAuthorizationPermission
                    }
                    userProfile={userProfile}
                  />
                }
                bulkActionButtons={false}
                empty={
                  <Empty emptyText="There are no related Contacts to be displayed" />
                }
                syncWithLocation
              >
                <InvestorContactsDatagrid permissions={permissions} />
              </List>
              <CopyContactStepper
                showDialog={showCopyContactsStepper}
                investorId={investorId}
                handleDialogToggle={handleShowCopyContactsStepper}
                userProfile={userProfile}
                {...rest}
              />
              {showPrimaryContacts && (
                <PrimaryContactsDialog
                  showDialog={showPrimaryContacts}
                  investorId={investorId}
                  handleDialogToggle={handleShowPrimaryContacts}
                  onSubmitEnd={props.onSubmitEnd}
                />
              )}
            </ViewTab>
          )}
          {userProfile.userType !== 'INVESTOR_ADMIN' && (
            <FormTab label="Matching Fields">
              <MatchingFields variant="standard" />
            </FormTab>
          )}
          {hasReadWireInstructionsPermission && (
            <ViewTab label="Wire Instructions">
              <TabSectionLabel label="Related Wire Instructions" />
              {/**
               * Using custom List component to avoid Redux filter issues
               * in cases when two List components use the filter prop.
               * Issue described and resolved in:
               * https://stackoverflow.com/questions/59883643/how-to-not-change-the-url-on-list-filtering-with-react-admin
               */}
              <List
                {...rest}
                style={{
                  margin: '0px',
                  width: '100%'
                }}
                filters={null}
                pagination={<ListPagination />}
                resource="wireinstructions"
                basePath="/wireinstructions"
                target="investorId"
                filter={{
                  investorId
                }}
                title=" " // this is necessary because framework was adding defaultTitle to form header
                actions={
                  <WireInstructionsActions
                    entityId={investorId}
                    handleWireInstructionCreateButtonClick={
                      handleWireInstructionCreateDialogToggle
                    }
                    hasEditPermission={hasEditPermission}
                    hasCreateWireInstructionPermission={
                      hasCreateWireInstructionPermission
                    }
                    hasExportWireInstructionExcelPermission={
                      hasExportWireInstructionExcelPermission
                    }
                    dataAvailable={wireInstructionPresent}
                  />
                }
                bulkActionButtons={
                  hasEditPermission &&
                  hasDeleteWireInstructionPermission && (
                    <BulkActionDeleteButton resource="wireinstructions" />
                  )
                }
                empty={
                  <Empty
                    emptyText="There are no Wires Instructions to be displayed"
                    buttons={[
                      <WireInstructionsImportFromExcel
                        key="wireinstructionimport"
                        hasImportWireInstructionExcelPermission={false}
                        hasExportWireInstructionExcelPermission={
                          hasExportWireInstructionExcelPermission
                        }
                        entityId={investorId}
                      />,
                      <CreateWireInstructionButton
                        key="createWireInstructionButton"
                        handleWireInstructionCreateButtonClick={
                          handleWireInstructionCreateDialogToggle
                        }
                        hasEditPermission={hasEditPermission}
                        hasCreateWireInstructionPermission={
                          hasCreateWireInstructionPermission
                        }
                      />
                    ]}
                  />
                }
              >
                <WireInstructionsDatagrid
                  handleOpen={handleWireInstructionUpdateDialogToggle}
                  permissions={permissions}
                  hasEditPermission={hasEditPermission}
                  setDataAvailable={handleWireInstructionPresent}
                />
              </List>

              {showWireInstructionCreateDialog && (
                <WireInstructionCreateUpdateDialog
                  {...rest}
                  title=" "
                  handleDialogToggle={handleWireInstructionCreateDialogToggle}
                  operation="Create"
                  popupTitle="New Wire Instruction"
                  okButtonLabel="Add"
                  investorId={props.id}
                  basePath="/wireinstructions"
                  resource="wireinstructions"
                />
              )}
              {showWireInstructionUpdateDialog && (
                <WireInstructionCreateUpdateDialog
                  {...rest}
                  operation="Update"
                  title=" "
                  popupTitle="Edit Wire Instruction"
                  okButtonLabel="Save"
                  hasDeleteWireInstructionPermission={
                    hasDeleteWireInstructionPermission
                  }
                  hasCreateWireInstructionPermission={
                    hasCreateWireInstructionPermission
                  }
                  investorId={props.id}
                  handleDialogToggle={handleWireInstructionUpdateDialogToggle}
                  basePath="/wireinstructions"
                  resource="wireinstructions"
                  id={wireInstructionRecord.id}
                  record={wireInstructionRecord}
                />
              )}
            </ViewTab>
          )}
          <ViewTab label="Properties">
            <UdfTab
              id={investorId}
              queryId="investorId"
              type="investor"
              hasEditPermission={hasEditPermission}
              {...rest}
            />
            <FormSpy
              subscription={{ dirty: true }}
              onChange={props => {
                setWhenBlockNavigation(props.dirty);
              }}
            />
          </ViewTab>
        </TabbedForm>
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(InvestorEdit);
